import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "./api";

const AuthCtx = createContext(null);

// The auth token itself is now stored in an httpOnly, SameSite=Lax cookie set
// by the backend. This module only mirrors the (non-sensitive) user profile
// into localStorage so we can render the shell instantly on cold reloads.
// The backend transparently renews the cookie on every request when the JWT
// has less than JWT_REFRESH_THRESHOLD_DAYS remaining, so an active user is
// perpetually logged in without ever seeing an OTP/re-login prompt.
const USER_KEY = "dentalai_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // Always hit /auth/me on mount — the cookie is the source of truth.
  // If we have a cached user but the cookie is gone, we quietly log out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!cancelled) {
          setUser(r.data);
          localStorage.setItem(USER_KEY, JSON.stringify(r.data));
        }
      } catch (err) {
        if (!cancelled && err?.response?.status === 401) {
          localStorage.removeItem(USER_KEY);
          setUser(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Listen for the `viyora:auth-expired` event fired by the axios interceptor
  // when a genuine session-level 401 occurs (as opposed to a permission 401).
  // We update React state and surface a friendly toast so the user knows why
  // they're being asked to sign in again — never "Invalid Credentials".
  useEffect(() => {
    const handler = (ev) => {
      const reason = ev?.detail?.reason;
      // Only tell the user if they WERE logged in (avoids double-toasting on
      // the login page itself when the /auth/me probe naturally 401s).
      if (user) {
        const msg = reason === "expired"
          ? "Your session expired. Please sign in again to continue."
          : "You've been signed out. Please sign in again.";
        toast.info(msg);
      }
      setUser(null);
    };
    window.addEventListener("viyora:auth-expired", handler);
    return () => window.removeEventListener("viyora:auth-expired", handler);
  }, [user]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const signup = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", payload);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* backend may be down; still clear locally */ }
    // Legacy cleanup — removes any residual tokens from the pre-cookie era.
    localStorage.removeItem("dentalai_token");
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem(USER_KEY, JSON.stringify(data));
      return data;
    } catch {
      return null;
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
